class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.isProcessing = false;
    this.shippingCache = new Map();
    this.latestShippingRates = [];
    this.shippingRequestToken = 0;
    this.shippingPollAttempts = 6;
    this.shippingPollDelay = 600;
    this.discountToggleTimer = null;
    this.selectors = {
      form: '#cart-drawer-form',
      quantityBtn: '.cart-drawer__quantity-btn',
      quantityInput: '.cart-drawer__quantity-input',
      removeBtn: '.cart-drawer__item-remove',
      closeBtn: '.js-close-drawer',
      loading: '[data-cart-loading]',
      discountTrigger: '[data-discount-trigger]',
      discountForm: '[data-discount-form]',
      discountInput: '[data-discount-input]',
      discountApply: '[data-discount-apply]',
      discountRemove: '[data-discount-remove]',
      discountMessage: '[data-discount-message]',
      shippingModule: '[data-shipping-module]',
      shippingProgress: '[data-shipping-progress]',
      shippingProgressBar: '[data-shipping-progress-bar]',
      shippingProgressText: '[data-shipping-progress-text]',
      summaryShippingRow: '[data-summary-shipping-row]',
      summaryShipping: '[data-summary-shipping]',
      summaryTotal: '[data-summary-total]',
      upsellAdd: '[data-upsell-add]'
    };
    this.translations = {
      discountApplied: this.dataset.i18nDiscountApplied || '',
      discountInvalid: this.dataset.i18nDiscountInvalid || '',
      discountEmpty: this.dataset.i18nDiscountEmpty || '',
      discountRemoved: this.dataset.i18nDiscountRemoved || '',
      shippingLoading: this.dataset.i18nShippingLoading || '',
      shippingError: this.dataset.i18nShippingError || '',
      shippingFrom: this.dataset.i18nShippingFrom || '',
      shippingFree: this.dataset.i18nShippingFree || '',
      shippingProgress: this.dataset.i18nShippingProgress || '',
      shippingThreshold: this.dataset.i18nShippingThreshold || ''
    };
    this.bindEvents();
    if (this.hasAttribute('open')) {
      this.initShippingEstimator();
    }
    this.updateDiscountPercentages();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.refreshDrawerViaEventHandler = this.handleDrawerRefreshViaEvent.bind(this);
    this.drawerClickHandler = this.handleClick.bind(this);
    this.drawerChangeHandler = this.handleChange.bind(this);
    this.drawerKeydownHandler = this.handleKeydown.bind(this);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.refreshDrawerViaEventHandler);
    this.addEventListener('click', this.drawerClickHandler);
    this.addEventListener('change', this.drawerChangeHandler);
    this.addEventListener('keydown', this.drawerKeydownHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      this.querySelectorAll('img').forEach((el) => {
        if (el.dataset.src) {
          el.src = el.dataset.src;
          el.removeAttribute('data-src');
        }
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshDrawerViaEventHandler);
    this.removeEventListener('click', this.drawerClickHandler);
    this.removeEventListener('change', this.drawerChangeHandler);
    this.removeEventListener('keydown', this.drawerKeydownHandler);
  }

  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
    this.initShippingEstimator();
  }

  handleDrawerRefreshViaEvent(evt) {
    const detail = evt.detail || {};
    const openAfter = Object.prototype.hasOwnProperty.call(detail, 'open') ? detail.open !== false : false;
    // Show subtle header loading indicator
    this.setLoading(true);
    this.syncStoredDiscountCode()
      .then((cart) =>
        this.refreshCartDrawer({
          openAfter,
          opener: detail.opener || null,
          cart: cart || detail.cart || null,
          bustCache: true
        })
      )
      .finally(() => {
        this.setLoading(false);
      });
  }

  handleClick(evt) {
    const closeBtn = evt.target.closest(this.selectors.closeBtn);
    if (closeBtn) {
      evt.preventDefault();
      this.close();
      return;
    }

    const removeBtn = evt.target.closest(this.selectors.removeBtn);
    if (removeBtn) {
      evt.preventDefault();
      const item = removeBtn.closest('[data-key]');
      if (!item) return;
      this.changeLineQuantity(item.dataset.key, 0);
      return;
    }

    // Discount trigger - toggle input form
    const discountTrigger = evt.target.closest(this.selectors.discountTrigger);
    if (discountTrigger) {
      evt.preventDefault();
      evt.stopPropagation();

      // Debounce to prevent double-toggle
      if (this.discountToggleTimer) {
        return;
      }

      this.discountToggleTimer = setTimeout(() => {
        this.discountToggleTimer = null;
      }, 300);

      this.toggleDiscountForm();
      return;
    }

    const discountApply = evt.target.closest(this.selectors.discountApply);
    if (discountApply) {
      evt.preventDefault();
      this.applyDiscountCode();
      return;
    }

    const discountRemove = evt.target.closest(this.selectors.discountRemove);
    if (discountRemove) {
      evt.preventDefault();
      console.log('[CartDrawer] Remove discount button clicked');
      this.removeDiscountCode();
      return;
    }

    const upsellAdd = evt.target.closest(this.selectors.upsellAdd);
    if (upsellAdd) {
      evt.preventDefault();
      const variantId = upsellAdd.dataset.upsellAdd;
      if (variantId) {
        this.addUpsellItem(variantId);
      }
      return;
    }

    const quantityBtn = evt.target.closest(this.selectors.quantityBtn);
    if (!quantityBtn) return;
    evt.preventDefault();
    const item = quantityBtn.closest('[data-key]');
    if (!item) return;
    const line = quantityBtn.dataset.line;
    const input = this.querySelector(`input[data-line="${line}"]`);
    let nextValue = parseInt(input?.value || '0', 10);
    if (!Number.isFinite(nextValue) || nextValue < 0) nextValue = 0;
    if (quantityBtn.dataset.action === 'increase') {
      nextValue += 1;
    } else if (quantityBtn.dataset.action === 'decrease') {
      nextValue = Math.max(0, nextValue - 1);
    }
    this.changeLineQuantity(item.dataset.key, nextValue);
  }

  handleChange(evt) {
    const input = evt.target.closest(this.selectors.quantityInput);
    if (!input) return;
    const item = input.closest('[data-key]');
    if (item) {
      let nextValue = parseInt(input.value || '0', 10);
      if (!Number.isFinite(nextValue) || nextValue < 0) nextValue = 0;
      this.changeLineQuantity(item.dataset.key, nextValue);
      return;
    }
  }

  handleKeydown(evt) {
    const quantityInput = evt.target.closest(this.selectors.quantityInput);
    if (quantityInput) {
      if (evt.key === 'Enter') {
        evt.preventDefault();
      }
      return;
    }

    const discountInput = evt.target.closest(this.selectors.discountInput);
    if (discountInput) {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        this.applyDiscountCode();
      }
    }
  }

  getLoader() {
    return this.querySelector(this.selectors.loading);
  }

  setLoading(isLoading) {
    const loader = this.getLoader();
    if (loader) loader.toggleAttribute('hidden', !isLoading);
    // No more full-drawer blur - just subtle header indicator
  }

  setItemLoading(itemKey, isLoading) {
    const item = this.querySelector(`[data-key="${itemKey}"]`);
    if (!item) return;
    const loader = item.querySelector('[data-item-loading]');
    if (loader) loader.toggleAttribute('hidden', !isLoading);
    item.classList.toggle('is-updating', isLoading);
  }

  setDiscountLoading(isLoading) {
    const spinner = this.querySelector('[data-discount-spinner]');
    if (spinner) spinner.toggleAttribute('hidden', !isLoading);
    const button = this.querySelector(this.selectors.discountApply);
    if (button) {
      button.disabled = isLoading;
      button.classList.toggle('is-disabled', isLoading);
    }
  }

  updateHeaderCount(cart) {
    if (!cart) return;
    const count = cart.item_count ?? 0;
    const desktopCount = document.querySelector('.header__cart-count-desktop');
    const mobileCount = document.querySelector('[data-cart-mobile-count]');
    if (desktopCount) {
      desktopCount.textContent = `(${count})`;
      desktopCount.classList.toggle('hidden', count === 0);
    }
    if (mobileCount) {
      mobileCount.textContent = count;
      mobileCount.toggleAttribute('hidden', count === 0);
    }
  }

  syncDataAttributes(newCartDrawer) {
    if (!newCartDrawer) return;
    Array.from(newCartDrawer.attributes).forEach((attr) => {
      if (attr.name.startsWith('data-')) {
        this.setAttribute(attr.name, attr.value);
      }
    });
  }

  initShippingEstimator() {
    this.shippingRequestToken += 1;
    const requestToken = this.shippingRequestToken;
    const module = this.querySelector(this.selectors.shippingModule);
    this.shippingModule = module || null;

    if (!module || !this.shouldEstimateShipping()) {
      if (module) module.hidden = true;
      return;
    }

    module.hidden = false;
    this.renderShippingLoading();

    this.fetchShippingRatesWithCache()
      .then((rates) => {
        if (requestToken !== this.shippingRequestToken) return;
        this.renderShippingResult(rates);
      })
      .catch(() => {
        if (requestToken !== this.shippingRequestToken) return;
        this.renderShippingError();
      });
  }

  updateDiscountPercentages() {
    const discountElements = this.querySelectorAll('[data-discount-percent]');
    discountElements.forEach((el) => {
      const discountAmount = parseInt(el.dataset.discountAmount || '0', 10);
      const cartSubtotal = parseInt(el.dataset.cartSubtotal || '0', 10);
      if (discountAmount > 0 && cartSubtotal > 0) {
        const percentage = Math.round((discountAmount / cartSubtotal) * 100);
        el.textContent = `-${percentage}%`;
      } else {
        el.textContent = '-';
      }
    });
  }

  shouldEstimateShipping() {
    if (!this.shippingModule) return false;
    const isGermany = this.isGermanyMarket();
    const totals = this.getCartTotals();
    return isGermany && totals.requiresShipping && totals.itemCount > 0;
  }

  isGermanyMarket() {
    const countryIso = (this.dataset.country || '').toUpperCase();
    const localeIso = (this.dataset.locale || '').toLowerCase();
    return countryIso === 'DE' || localeIso.startsWith('de');
  }

  getShippingDestination() {
    // Always use English country name for the Shipping Rates API
    // The localized name (e.g., 'Deutschland') doesn't work with the API
    const countryCode = (this.dataset.country || 'DE').toUpperCase();
    const countryNameMap = {
      'DE': 'Germany',
      'AT': 'Austria',
      'CH': 'Switzerland'
    };
    return {
      country: countryNameMap[countryCode] || 'Germany',
      countryCode: countryCode,
      province: this.dataset.customerProvince || '',
      city: this.dataset.customerCity || '',
      zip: this.dataset.customerZip || '',
      locale: this.dataset.locale || ''
    };
  }

  buildShippingParams(destination) {
    const params = new URLSearchParams();
    params.set('shipping_address[country]', destination.country || 'Germany');
    params.set('shipping_address[country_code]', destination.countryCode || 'DE');
    const zip = destination.zip || (destination.countryCode === 'DE' ? '10115' : '');
    if (zip) {
      params.set('shipping_address[zip]', zip);
    }
    if (destination.province) {
      params.set('shipping_address[province]', destination.province);
    }
    if (destination.city) {
      params.set('shipping_address[city]', destination.city);
    }
    if (destination.locale) {
      params.set('locale', destination.locale);
    }
    return params;
  }

  fetchShippingRatesWithCache() {
    const destination = this.getShippingDestination();
    const totals = this.getCartTotals();
    const cacheKey = `${destination.countryCode || 'DE'}-${destination.province || 'any'}-${totals.total}-${totals.itemCount}`;
    const cached = this.shippingCache.get(cacheKey);
    const now = Date.now();
    if (cached && cached.expiresAt > now) {
      return Promise.resolve(cached.rates);
    }
    return this.requestShippingRates(destination, cacheKey);
  }

  requestShippingRates(destination, cacheKey) {
    const params = this.buildShippingParams(destination);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(`${root}cart/prepare_shipping_rates.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json'
      },
      body: params.toString()
    })
      .catch(() => { })
      .then(() => this.pollShippingRates(params))
      .catch(() => this.fetchShippingRatesDirect(params))
      .then((rates) => {
        const safeRates = Array.isArray(rates) ? rates : [];
        this.shippingCache.set(cacheKey, {
          rates: safeRates,
          expiresAt: Date.now() + 5 * 60 * 1000
        });
        return safeRates;
      });
  }

  pollShippingRates(params, attempt = 0) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}cart/async_shipping_rates.json?${params.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length > 0) {
          return data.shipping_rates;
        }
        if (attempt >= this.shippingPollAttempts) {
          return this.fetchShippingRatesDirect(params);
        }
        return this.delay(this.shippingPollDelay).then(() => this.pollShippingRates(params, attempt + 1));
      });
  }

  fetchShippingRatesDirect(params) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}cart/shipping_rates.json?${params.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((res) => res.json())
      .then((data) => data?.shipping_rates || []);
  }

  renderShippingLoading() {
    const progress = this.querySelector(this.selectors.shippingProgress);
    const summaryRow = this.querySelector(this.selectors.summaryShippingRow);
    const summaryTotal = this.querySelector(this.selectors.summaryTotal);
    const totals = this.getCartTotals();
    const policies = this.querySelector('[data-policies-hide-when-shipping]');
    if (progress) progress.hidden = true;
    if (summaryRow) summaryRow.hidden = true;
    if (summaryTotal) summaryTotal.textContent = this.formatMoney(totals.total);
    if (policies) policies.style.display = '';
  }

  renderShippingError() {
    const progress = this.querySelector(this.selectors.shippingProgress);
    const summaryRow = this.querySelector(this.selectors.summaryShippingRow);
    const summaryTotal = this.querySelector(this.selectors.summaryTotal);
    const totals = this.getCartTotals();
    const policies = this.querySelector('[data-policies-hide-when-shipping]');
    if (progress) progress.hidden = true;
    if (summaryRow) summaryRow.hidden = true;
    if (summaryTotal) summaryTotal.textContent = this.formatMoney(totals.total);
    if (policies) policies.style.display = '';
  }

  renderShippingResult(rates = []) {
    if (!this.shippingModule) return;
    if (!Array.isArray(rates) || rates.length === 0) {
      this.renderShippingError();
      return;
    }

    const parsedRates = rates
      .map((rate) => {
        return {
          ...rate,
          price_cents: this.parseRatePriceToCents(rate.price)
        };
      })
      .filter((rate) => Number.isFinite(rate.price_cents));

    if (parsedRates.length === 0) {
      this.renderShippingError();
      return;
    }

    const sortedRates = [...parsedRates].sort((a, b) => a.price_cents - b.price_cents);
    const cheapestRate = sortedRates[0];
    const cheapestPaidRate = sortedRates.find((rate) => rate.price_cents > 0) || cheapestRate;

    const progress = this.querySelector(this.selectors.shippingProgress);
    const progressText = this.querySelector(this.selectors.shippingProgressText);
    const progressBar = this.querySelector(this.selectors.shippingProgressBar);
    const summaryRow = this.querySelector(this.selectors.summaryShippingRow);
    const summaryShipping = this.querySelector(this.selectors.summaryShipping);
    const summaryTotal = this.querySelector(this.selectors.summaryTotal);
    const policies = this.querySelector('[data-policies-hide-when-shipping]');
    this.latestShippingRates = sortedRates;

    const hasFreeRate = sortedRates.some((rate) => rate.price_cents === 0);
    const minPrice = cheapestRate.price_cents;
    const standardShippingPrice = cheapestPaidRate.price_cents;
    const threshold = this.getFreeShippingThreshold();
    const totals = this.getCartTotals();
    const qualifiesForFree = hasFreeRate || (threshold && totals.total >= threshold);

    // Always use the cheapest shipping rate
    let shippingCost = minPrice;

    if (qualifiesForFree) {
      shippingCost = 0;
      if (progress) progress.hidden = true;
    }

    if (!qualifiesForFree && threshold && totals.total < threshold) {
      const deficit = Math.max(0, threshold - totals.total);
      if (progress && progressText && progressBar) {
        progress.hidden = false;
        const template = this.translations.shippingProgress || '';
        progressText.textContent = template.replace('[[amount]]', this.formatMoney(deficit));

        // Calculate visual progress with psychological "almost there" effect
        // When €19 or less remains (1900 cents), show bar at fixed 98%
        const nearThresholdAmount = 1900; // €19 in cents
        let visualRatio;

        if (deficit <= nearThresholdAmount && totals.total > 0) {
          // When within €19 of free shipping, show bar at fixed 98%
          visualRatio = 0.98;
        } else {
          // Normal proportional progress, but cap at 92% (the "near zone" starts there)
          const normalRatio = totals.total / threshold;
          const maxNormalRatio = (threshold - nearThresholdAmount) / threshold;
          visualRatio = Math.min(normalRatio, maxNormalRatio) * (0.92 / maxNormalRatio);
          visualRatio = Math.max(0, Math.min(0.92, visualRatio));
        }

        progressBar.style.width = `${Math.round(visualRatio * 100)}%`;
      }
    } else if (progress) {
      progress.hidden = true;
    }

    // Update summary row with shipping cost
    if (summaryRow && summaryShipping) {
      summaryRow.hidden = false;
      if (shippingCost === 0) {
        // Show as discount like the other discounts
        summaryShipping.className = 'cart-drawer__summary-value cart-drawer__summary-value--discount';
        const configuredBaseline = this.getConfiguredBaselineShippingCents();
        const baselineShipping = configuredBaseline || standardShippingPrice;
        summaryShipping.textContent = `–${this.formatMoney(baselineShipping)}`;
      } else {
        summaryShipping.className = 'cart-drawer__summary-value';
        summaryShipping.textContent = this.formatMoney(shippingCost);
      }
    }

    if (summaryTotal) {
      summaryTotal.textContent = this.formatMoney(totals.total + shippingCost);
    }

    // Hide policies when shipping is calculated
    if (policies) policies.style.display = 'none';
  }

  renderShippingRatesList(rates) {
    // No longer needed - only showing cheapest rate
  }

  getCartTotals() {
    const totalRaw = this.shippingModule ? this.shippingModule.dataset.cartTotal : '0';
    const itemCountRaw = this.shippingModule ? this.shippingModule.dataset.cartItemCount : '0';
    const requiresShippingRaw = this.shippingModule ? this.shippingModule.dataset.cartRequiresShipping : 'false';
    const total = parseInt(totalRaw, 10);
    const itemCount = parseInt(itemCountRaw, 10);
    return {
      total: Number.isFinite(total) ? total : 0,
      itemCount: Number.isFinite(itemCount) ? itemCount : 0,
      requiresShipping: requiresShippingRaw === 'true'
    };
  }

  getFreeShippingThreshold() {
    if (!this.shippingModule) return null;
    const raw = this.shippingModule.dataset.freeShippingThreshold;
    if (raw === '' || raw === undefined) return null;
    const value = parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  formatMoney(cents) {
    const moneyFormat = this.dataset.moneyFormat || (window.Shopify && window.Shopify.money_format) || '{{amount}}';
    const currency = (this.shippingModule && this.shippingModule.dataset.cartCurrency) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, moneyFormat);
      } catch (e) {
        // fall through to the fallback formatter
      }
    }

    const rawValue = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const centsValue = Number.isFinite(rawValue) ? rawValue : 0;

    const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
      const amount = (value / 100).toFixed(precision);
      const parts = amount.split('.');
      const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const decimalPart = parts[1] && precision > 0 ? `${decimal}${parts[1]}` : '';
      return `${integer}${decimalPart}`;
    };

    const placeholderMatch = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';
    let formattedAmount;

    switch (placeholder) {
      case 'amount_no_decimals':
        formattedAmount = formatWithDelimiters(centsValue, 0);
        break;
      case 'amount_with_comma_separator':
        formattedAmount = formatWithDelimiters(centsValue, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedAmount = formatWithDelimiters(centsValue, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formattedAmount = formatWithDelimiters(centsValue, 2, "'", '.');
        break;
      case 'amount':
      default:
        formattedAmount = formatWithDelimiters(centsValue, 2);
        break;
    }

    if (placeholderMatch) {
      return moneyFormat.replace(placeholderMatch[0], formattedAmount);
    }

    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(centsValue / 100);
    } catch (e) {
      return `${centsValue / 100} ${currency}`;
    }
  }

  getConfiguredBaselineShippingCents() {
    if (!this.shippingModule) return null;
    const raw = this.shippingModule.dataset.freeShippingDiscountBaselineCents;
    if (raw === '' || raw === undefined) return null;
    const value = parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  delay(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  parseRatePriceToCents(price) {
    if (typeof price === 'number') {
      if (Number.isInteger(price)) return price;
      return Math.round(price * 100);
    }
    if (typeof price === 'string') {
      const normalized = price.replace(',', '.').trim();
      const value = parseFloat(normalized);
      if (Number.isFinite(value)) {
        return Math.round(value * 100);
      }
    }
    return 0;
  }

  refreshCartDrawer({ openAfter = false, opener = null, cart = null, bustCache = false } = {}) {
    let url = `${window.Shopify.routes.root}cart?section_id=cart-drawer`;
    if (bustCache) {
      url += `&t=${Date.now()}`;
    }
    return fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newCartDrawer = html.querySelector('cart-drawer');
        if (!newCartDrawer) return;
        const wasOpen = this.hasAttribute('open');
        this.syncDataAttributes(newCartDrawer);
        this.innerHTML = newCartDrawer.innerHTML;
        if (openAfter || wasOpen) {
          this.open(opener);
        }
        const nextCount = parseInt(newCartDrawer.dataset.cartCount || '', 10);
        if (Number.isFinite(nextCount)) {
          this.updateHeaderCount({ item_count: nextCount });
        } else if (cart) {
          this.updateHeaderCount(cart);
        }
        if (openAfter || wasOpen) {
          this.initShippingEstimator();
        }
        this.updateDiscountPercentages();
      });
  }

  getStoredDiscountCode() {
    try {
      return window.localStorage.getItem('abayastore:applied-discount-code') || '';
    } catch (err) {
      return '';
    }
  }

  setStoredDiscountCode(code) {
    try {
      window.localStorage.setItem('abayastore:applied-discount-code', code);
    } catch (err) {}
  }

  clearStoredDiscountCode() {
    try {
      window.localStorage.removeItem('abayastore:applied-discount-code');
    } catch (err) {}
  }

  syncStoredDiscountCode() {
    const code = this.getStoredDiscountCode().trim();
    if (!code) return Promise.resolve(null);

    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ discount: code })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Discount sync failed');
        return res.json();
      })
      .then((cart) => this.delay(150).then(() => cart))
      .catch(() => null);
  }

  changeLineQuantity(lineKey, quantity) {
    if (!lineKey) return Promise.resolve();
    this.setItemLoading(lineKey, true);
    return fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: lineKey, quantity })
    })
      .then((res) => res.json())
      .then((cart) => {
        this.updateHeaderCount(cart);
        return this.syncStoredDiscountCode().then((syncedCart) =>
          this.refreshCartDrawer({ openAfter: true, cart: syncedCart || cart, bustCache: true })
        );
      })
      .catch(() => { })
      .finally(() => {
        this.setItemLoading(lineKey, false);
      });
  }

  showDiscountMessage(message, type = 'error') {
    const messageEl = this.querySelector(this.selectors.discountMessage);
    if (!messageEl) return;
    messageEl.textContent = message;
    messageEl.classList.remove('cart-drawer__discount-message--error', 'cart-drawer__discount-message--success');
    messageEl.classList.add(`cart-drawer__discount-message--${type}`);
    messageEl.hidden = false;

    setTimeout(() => {
      messageEl.hidden = true;
    }, 5000);
  }

  clearDiscountMessage() {
    const messageEl = this.querySelector(this.selectors.discountMessage);
    if (messageEl) {
      messageEl.hidden = true;
    }
  }

  toggleDiscountForm() {
    const form = this.querySelector(this.selectors.discountForm);
    const trigger = this.querySelector(this.selectors.discountTrigger);
    const input = this.querySelector(this.selectors.discountInput);

    if (form && trigger) {
      const isHidden = form.hasAttribute('hidden');
      form.toggleAttribute('hidden', !isHidden);
      trigger.setAttribute('aria-expanded', isHidden ? 'true' : 'false');

      // Focus input when opening
      if (isHidden && input) {
        setTimeout(() => input.focus(), 100);
      }
    }
  }

  applyDiscountCode() {
    const input = this.querySelector(this.selectors.discountInput);
    if (!input) return;

    const code = input.value.trim();
    if (!code) {
      this.showDiscountMessage(this.translations.discountEmpty, 'error');
      return;
    }

    // Prevent double-submit
    if (this.isProcessing) {
      return;
    }
    this.isProcessing = true;

    this.clearDiscountMessage();
    this.setDiscountLoading(true);

    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ discount: code })
    })
      .then((res) => res.json())
      .then((cart) => {
        this.setStoredDiscountCode(code);
        this.updateHeaderCount(cart);
        return this.delay(150).then(() => this.refreshCartDrawer({ openAfter: true, cart, bustCache: true }));
      })
      .then(() => {
        // Clear input after refresh
        const freshInput = this.querySelector(this.selectors.discountInput);
        if (freshInput) freshInput.value = '';

        // Check DOM to see if discount was applied
        // Check for both cart-level discounts (data-discount-remove) and item-level discounts (.cart-drawer__item-discounts)
        const hasCartLevelDiscount = this.querySelector(this.selectors.discountRemove);
        const hasItemLevelDiscount = this.querySelector('.cart-drawer__item-discounts');
        if (!hasCartLevelDiscount && !hasItemLevelDiscount) {
          // No discount visible after refresh - code may be invalid
          this.showDiscountMessage(this.translations.discountInvalid, 'error');
        }
      })
      .catch(() => {
        this.showDiscountMessage(this.translations.discountInvalid, 'error');
      })
      .finally(() => {
        this.setDiscountLoading(false);
        this.isProcessing = false;
      });
  }

  removeDiscountCode() {
    // Prevent double-submit
    if (this.isProcessing) {
      console.log('[CartDrawer] Already processing, skipping...');
      return;
    }
    this.isProcessing = true;

    this.clearDiscountMessage();
    this.setDiscountLoading(true);

    console.log('[CartDrawer] Removing discount code...');

    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ discount: '' })
    })
      .then((res) => res.json())
      .then((cart) => {
        console.log('[CartDrawer] Discount remove response:', cart);
        console.log('[CartDrawer] Active discounts after remove:', cart.cart_level_discount_applications);
        this.clearStoredDiscountCode();
        document.dispatchEvent(new CustomEvent('discount:removed'));
        this.updateHeaderCount(cart);
        return this.refreshCartDrawer({ openAfter: true, cart, bustCache: true });
      })
      .catch((err) => {
        console.error('[CartDrawer] Discount remove error:', err);
      })
      .finally(() => {
        this.setDiscountLoading(false);
        this.isProcessing = false;
      });
  }

  addUpsellItem(variantId) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    this.setLoading(true);

    return fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
      .then((res) => res.json())
      .then((cart) => {
        this.updateHeaderCount(cart);
        return this.syncStoredDiscountCode().then((syncedCart) =>
          this.refreshCartDrawer({ openAfter: true, cart: syncedCart || cart, bustCache: true })
        );
      })
      .catch((err) => {
        console.error('[CartDrawer] Upsell add error:', err);
      })
      .finally(() => {
        this.setLoading(false);
        this.isProcessing = false;
      });
  }
}

window.customElements.define('cart-drawer', CartDrawer);
